// Side-effect import: ensures Amplify.configure runs before any aws-amplify
// API is called from this module. ES `import` statements all hoist before
// any plain statement, so we make sure THIS module's first import is the
// configure side-effect.
import "@/lib/amplify-config";

import { useCallback, useEffect, useState } from "react";
import { type AxiosError } from "axios";
import { api } from "@/lib/api";
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  type AuthUser,
} from "aws-amplify/auth";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export type AuthState = {
  status: AuthStatus;
  user: AuthUser | null;
  email: string | null;
};

let cachedState: AuthState = { status: "loading", user: null, email: null };
let refreshStarted = false;
const listeners = new Set<(s: AuthState) => void>();

function publish(next: AuthState) {
  cachedState = next;
  listeners.forEach((fn) => fn(next));
}

async function refresh(): Promise<void> {
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    const email = (session.tokens?.idToken?.payload?.email as string | undefined) ?? null;
    publish({ status: "authenticated", user, email });
  } catch {
    publish({ status: "anonymous", user: null, email: null });
  }
}

function ensureRefreshStarted(): void {
  if (refreshStarted) return;
  refreshStarted = true;
  void refresh();
}

export function useAuth() {
  const [state, setState] = useState<AuthState>(cachedState);

  useEffect(() => {
    ensureRefreshStarted();
    listeners.add(setState);
    setState(cachedState);
    return () => {
      listeners.delete(setState);
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await amplifySignIn({ username: email, password });
    await refresh();
    return result;
  }, []);

  const confirm = useCallback(async (email: string, code: string) => {
    return confirmSignUp({ username: email, confirmationCode: code });
  }, []);

  const resend = useCallback(async (email: string) => {
    return resendSignUpCode({ username: email });
  }, []);

  const signOut = useCallback(async () => {
    await amplifySignOut();
    await refresh();
  }, []);

  const forgot = useCallback(async (email: string) => {
    return resetPassword({ username: email });
  }, []);

  const confirmForgot = useCallback(async (email: string, code: string, newPassword: string) => {
    return confirmResetPassword({ username: email, confirmationCode: code, newPassword });
  }, []);

  const requestOtp = useCallback(async (email: string, roleIntent?: "STUDENT" | "PARENT") => {
    // Email-OTP doubles as sign-up. Cognito's PreventUserExistenceErrors hides
    // the email from the CreateAuthChallenge trigger for brand-new addresses, so
    // the account can't be created inside the trigger. Provision it first via the
    // backend (idempotent + always 200, so it leaks no existence signal), THEN
    // start the client-side custom-auth challenge, which is what emails the code.
    try {
      await api.post("/auth/otp/request", {
        email,
        ...(roleIntent ? { role_intent: roleIntent } : {}),
      });
    } catch (err) {
      const message = (err as AxiosError<{ status_message?: string }>).response?.data?.status_message;
      throw new Error(message || "Could not start sign-in. Please try again in a moment.");
    }
    const result = await amplifySignIn({
      username: email,
      options: { authFlowType: "CUSTOM_WITHOUT_SRP" },
    });
    if (result.isSignedIn) await refresh();
    return result;
  }, []);

  const verifyOtp = useCallback(async (code: string) => {
    const result = await confirmSignIn({ challengeResponse: code });
    if (result.isSignedIn) await refresh();
    return result;
  }, []);

  return {
    ...state,
    refresh,
    signIn,
    confirm,
    resend,
    signOut,
    forgot,
    confirmForgot,
    requestOtp,
    verifyOtp,
  };
}
